// src/components/Admin/Auth/RegisterForm/RegisterForm.js
import React, { useState, useCallback } from "react";
import { Image, Form, Button, Dimmer, Loader } from "semantic-ui-react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Auth } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./RegisterForm.Form";
import { toast, ToastContainer } from "react-toastify";
import { useDropzone } from "react-dropzone";
import { image } from "../../../../assets";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "./RegisterForm.scss";
import { countries } from "../../../../contexts/countries";

const authController = new Auth();

export function RegisterForm({ openLogin }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [verificationSent, setVerificationSent] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(["auth", "common"]);

  // Decide la ruta tras /portal/ según rol
  // Fase 1A M4 - K12 Free Refactor: free y pagado entran a /teacher/salas.
  const getPortalPath = (user) => {
    const { role } = user;
    if (role === "teacher") {
      return "teacher/salas";
    }
    return "";
  };

  // 1) Registro rápido con Google
  const handleGoogleSuccess = async ({ credential }) => {
    try {
      setLoading(true);
      const result = await authController.loginWithGoogle(credential, {
        emailMarketingConsent: formik.values.marketingConsent === true,
      });
      const data = result.data ?? result;
      const { accessToken, refreshToken, user: userData, isNewUser } = data;

      if (!accessToken || !refreshToken || !userData) {
        throw new Error(t("auth:errors.googleLoginInvalid"));
      }

      authController.setAccessToken(accessToken);
      authController.setRefreshToken(refreshToken);
      await login(accessToken, userData);

      // Fase 1A M5 - K12 Free: si es alta nueva, redirigir al onboarding
      // /welcome (3 pasos). Si ya existia (re-login Google), comportamiento
      // habitual: ir al portal del rol.
      const portalPath = isNewUser ? "portal/welcome" : getPortalPath(userData);
      navigate(`/${portalPath}`, { replace: true });
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      setLoading(false);
      toast.error(error.message || t("auth:register.googleError"), {
        position: "top-center",
        autoClose: false,
      });
    }
  };

  // 2) Registro por formulario
  const formik = useFormik({
    initialValues: initialValues(),
    validationSchema: validationSchema(verificationSent, t),
    validateOnChange: false,
    onSubmit: async () => {
      try {
        if (!verificationSent) {
          // Paso 1: envío de datos + avatar
          const formData = new FormData();
          formData.append("firstname", formik.values.firstname);
          formData.append("lastname", formik.values.lastname);
          formData.append("country", formik.values.country);
          formData.append("address", formik.values.address);
          formData.append("phone", formik.values.phone);
          formData.append("role", formik.values.role);
          formData.append("holded", formik.values.holded);
          formData.append("email", formik.values.email);
          formData.append("password", formik.values.password);
          // Opt-in de marketing: viaja como string en multipart; el backend
          // normaliza "true" -> true.
          formData.append(
            "emailMarketingConsent",
            formik.values.marketingConsent ? "true" : "false",
          );
          if (formik.values.fileAvatar) {
            formData.append("avatar", formik.values.fileAvatar);
          }
          const response = await authController.register(formData);
          setPendingToken(response.pendingToken);
          setVerificationSent(true);
          toast.success(t("auth:register.successCode"));
        } else {
          // Paso 2: verificación de código
          const codeNumber = formik.values.code;
          const verificationResponse = await authController.verifyRegistration(
            pendingToken,
            codeNumber,
          );
          // Fase 1A M5 - K12 Free: el alta free devuelve
          // {user, accessToken, refreshToken, isNewUser}. Otros roles
          // (partner, user, etc) devuelven {access, user_id}. Soportamos
          // ambos formatos y auto-logueamos cuando hay token.
          const accessToken =
            verificationResponse.accessToken || verificationResponse.access;
          const userData = verificationResponse.user;
          const refreshTokenFree = verificationResponse.refreshToken;
          const { isNewUser } = verificationResponse;

          if (accessToken && userData) {
            // Alta free: auto-login + redirect a /welcome si es nueva
            authController.setAccessToken(accessToken);
            if (refreshTokenFree) {
              authController.setRefreshToken(refreshTokenFree);
            }
            await login(accessToken, userData);
            toast.success(t("auth:register.successVerified"));
            const portalPath = isNewUser
              ? "portal/welcome"
              : "portal/teacher/salas";
            navigate(`/${portalPath}`, { replace: true });
            setTimeout(() => window.location.reload(), 500);
          } else {
            // Otros roles: comportamiento previo
            authController.setAccessToken(verificationResponse.access);
            authController.setUserId(verificationResponse.user_id);
            toast.success(t("auth:register.successVerified"));
            openLogin();
          }
        }
      } catch (error) {
        toast.error(`Error: ${error.message}`, {
          position: "top-center",
          autoClose: false,
          closeOnClick: true,
        });
      }
    },
  });

  // Dropzone para avatar
  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const validMimeTypes = ["image/jpeg", "image/png"];
        if (validMimeTypes.includes(file.type)) {
          formik.setFieldValue("avatar", URL.createObjectURL(file));
          formik.setFieldValue("fileAvatar", file);
        } else {
          console.error("Formato inválido. Debe ser JPG o PNG.");
        }
      }
    },
    [formik],
  );
  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/jpeg": [".jpeg", ".jpg"], "image/png": [".png"] },
    onDrop,
    multiple: false,
  });

  // Helper para mostrar avatar
  const getAvatar = () => {
    if (formik.values.fileAvatar) return formik.values.avatar;
    if (formik.values.avatar) return `${formik.values.avatar}`;
    return image.noAvatar;
  };

  return (
    <>
      <ToastContainer />

      {loading && (
        <Dimmer active inverted page>
          <Loader content={t("common:actions.loading")} />
        </Dimmer>
      )}

      <Form onSubmit={formik.handleSubmit}>
        {!verificationSent && (
          <>
            <div className="user-form__avatar" {...getRootProps()}>
              <input {...getInputProps()} />
              <Image avatar size="small" src={getAvatar()} />
            </div>

            <Form.Input
              name="firstname"
              type="text"
              placeholder={t("auth:register.firstnamePlaceholder")}
              onChange={formik.handleChange}
              value={formik.values.firstname}
              error={
                formik.errors.firstname
                  ? { content: formik.errors.firstname }
                  : null
              }
            />
            <Form.Input
              name="lastname"
              type="text"
              placeholder={t("auth:register.lastnamePlaceholder")}
              onChange={formik.handleChange}
              value={formik.values.lastname}
              error={
                formik.errors.lastname
                  ? { content: formik.errors.lastname }
                  : null
              }
            />
            <Form.Dropdown
              name="country"
              placeholder={t("auth:register.countryPlaceholder")}
              fluid
              selection
              options={countries}
              onChange={(e, data) =>
                formik.setFieldValue("country", data.value)
              }
              value={formik.values.country}
              error={
                formik.errors.country
                  ? { content: formik.errors.country }
                  : null
              }
            />
            <Form.Dropdown
              name="role"
              placeholder={t("auth:register.accountTypePlaceholder")}
              fluid
              selection
              options={[
                {
                  key: "free",
                  value: "free",
                  text: t("auth:register.accountFree"),
                },
              ]}
              onChange={(e, data) => formik.setFieldValue("role", data.value)}
              value={formik.values.role}
              error={
                formik.errors.role ? { content: formik.errors.role } : null
              }
            />
            {formik.values.role === "partner" && (
              <Form.Input
                name="holded"
                type="text"
                placeholder={t("auth:register.holdedPlaceholder")}
                onChange={formik.handleChange}
                value={formik.values.holded}
                error={
                  formik.errors.holded
                    ? { content: formik.errors.holded }
                    : null
                }
              />
            )}
            <Form.Input
              name="email"
              type="text"
              placeholder={t("auth:register.emailPlaceholder")}
              onChange={formik.handleChange}
              value={formik.values.email}
              error={
                formik.errors.email ? { content: formik.errors.email } : null
              }
            />
            <Form.Input
              name="password"
              type="password"
              placeholder={t("auth:register.passwordPlaceholder")}
              onChange={formik.handleChange}
              value={formik.values.password}
              error={
                formik.errors.password
                  ? { content: formik.errors.password }
                  : null
              }
            />
            <Form.Input
              name="repeatPassword"
              type="password"
              placeholder={t("auth:register.repeatPasswordPlaceholder")}
              onChange={formik.handleChange}
              value={formik.values.repeatPassword}
              error={
                formik.errors.repeatPassword
                  ? { content: formik.errors.repeatPassword }
                  : null
              }
            />
            <Form.Checkbox
              name="conditionsAccepted"
              label={t("auth:register.termsCheckbox")}
              onChange={(e, data) =>
                formik.setFieldValue("conditionsAccepted", data.checked)
              }
              checked={formik.values.conditionsAccepted}
              error={
                formik.errors.conditionsAccepted
                  ? { content: formik.errors.conditionsAccepted }
                  : null
              }
            />
            <Form.Checkbox
              name="marketingConsent"
              label={t("auth:register.marketingCheckbox")}
              onChange={(e, data) =>
                formik.setFieldValue("marketingConsent", data.checked)
              }
              checked={formik.values.marketingConsent}
            />
            {/* Aviso soft opt-in (LSSI 21.2): los correos del servicio (drip
                de la prueba) no dependen de la casilla; baja 1-click. */}
            <p style={{ fontSize: 12, color: "#8a94a6", margin: "4px 0 12px" }}>
              {t("auth:register.serviceEmailsNotice")}
            </p>

            <Button type="submit" primary fluid loading={formik.isSubmitting}>
              {t("auth:register.submitButton")}
            </Button>

            {/* NUEVO: registro rápido con Google */}
            <div className="divider-text">
              {t("auth:register.registerWith")}
            </div>
            <div style={{ marginTop: "1em", textAlign: "center" }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => {
                  setLoading(false);
                  toast.error(t("auth:register.googleError"));
                }}
              />
            </div>
          </>
        )}

        {verificationSent && (
          <>
            <p>{t("auth:register.verificationPrompt")}</p>
            <Form.Input
              name="code"
              type="text"
              placeholder={t("auth:register.codePlaceholder")}
              onChange={formik.handleChange}
              value={formik.values.code}
              error={
                formik.errors.code ? { content: formik.errors.code } : null
              }
            />
            <Button type="submit" primary fluid loading={formik.isSubmitting}>
              {t("auth:register.confirmButton")}
            </Button>
          </>
        )}
      </Form>
    </>
  );
}
